import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { GetAdminEmployeesOutputType } from 'zite-endpoints-sdk';

type Employee = GetAdminEmployeesOutputType['employees'][0];

interface Props {
  employees: Employee[];
  loading: boolean;
  onSelectEmployee: (emp: Employee) => void;
}

const statusColors: Record<string, string> = {
  'Not Started': 'text-muted-foreground bg-muted border-border',
  'In Progress': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  'Submitted': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  'Approved': 'text-green-500 bg-green-500/10 border-green-500/20',
};

export default function EmployeeTable({ employees, loading, onSelectEmployee }: Props) {
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchesSearch = !q || [`${e.firstName} ${e.lastName}`, e.email || '', e.position || ''].some(f => f.toLowerCase().includes(q));
    const matchesDept = deptFilter === 'all' || e.department === deptFilter;
    return matchesSearch && matchesDept;
  });

  if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..." className="pl-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-44 shrink-0"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            <SelectItem value="Front of House">Front of House</SelectItem>
            <SelectItem value="Back of House">Back of House</SelectItem>
            <SelectItem value="Kitchen">Kitchen</SelectItem>
            <SelectItem value="Management">Management</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Name</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden md:table-cell">Position</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Department</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Onboarding</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(emp => (
              <tr key={emp.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => onSelectEmployee(emp)}>
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground">{emp.firstName} {emp.lastName}</p>
                  <p className="text-xs text-muted-foreground">{emp.email}</p>
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{emp.position || '—'}</td>
                <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{emp.department || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 border rounded-sm font-medium ${emp.stepsCompleted >= 7 ? 'bg-green-500/10 text-green-500 border-green-500/20' : emp.stepsCompleted > 0 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-muted text-muted-foreground border-border'}`}>
                    {emp.stepsCompleted}/{emp.totalSteps}
                  </span>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span className={`text-xs px-2 py-0.5 border rounded-sm ${statusColors[emp.onboardingStatus ?? 'Not Started'] || statusColors['Not Started']}`}>{emp.onboardingStatus}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={e => { e.stopPropagation(); onSelectEmployee(emp); }}>View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center py-8 text-muted-foreground text-sm">No employees found</p>}
      </div>
    </div>
  );
}
